import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Calendar as CalIcon, User, ClipboardList, CheckCircle2,
  Plus, X, Clock, Loader2, ArrowLeft, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useBooking } from "../lib/bookingStore";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Calendar } from "../components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import Invoice from "../components/Invoice";

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Booking() {
  const { t, lang, L } = useApp();
  const nav = useNavigate();
  const { services, offer, addService, removeService, setOffer, clear, subtotal } = useBooking();
  const [step, setStep] = useState(1);
  const [allServices, setAllServices] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");

  const [customer, setCustomer] = useState({ name: "", phone: "", email: "", notes: "", contact_method: "whatsapp" });
  const [agree, setAgree] = useState(false);
  const [payMethod, setPayMethod] = useState("pay_at_salon");
  const [payConfig, setPayConfig] = useState({ enabled: false });

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [moyasarBooking, setMoyasarBooking] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setAllServices(r.data)).catch(() => {});
    api.get("/payments/config").then((r) => setPayConfig(r.data)).catch(() => {});
  }, []);

  const discount = offer ? Math.max(0, Number(offer.old_price || 0) - Number(offer.new_price || 0)) : 0;
  const sub = subtotal();
  const total = Math.max(0, sub - discount);

  const steps = [
    { n: 1, label: t.booking.step1, icon: Sparkles },
    { n: 2, label: t.booking.step2, icon: CalIcon },
    { n: 3, label: t.booking.step3, icon: User },
    { n: 4, label: t.booking.step4, icon: ClipboardList },
    { n: 5, label: t.booking.step5, icon: CheckCircle2 },
  ];

  const loadSlots = (d) => {
    setSlots(null); setTime("");
    api.get(`/availability?date=${fmt(d)}`).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  };

  const canNext = useMemo(() => {
    if (step === 1) return services.length > 0;
    if (step === 2) return date && time;
    if (step === 3) return customer.name && customer.phone && /\S+@\S+\.\S+/.test(customer.email);
    if (step === 4) return agree;
    return true;
  }, [step, services, date, time, customer, agree]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        services: services.map((s) => s.id),
        date: fmt(date),
        time,
        customer,
        offer_id: offer?.id || null,
        agree_policy: agree,
        pay_online: payMethod === "pay_online" && payConfig.enabled,
      };
      const { data } = await api.post("/bookings", payload);
      if (data.pay_online) {
        setMoyasarBooking(data.booking);
        setStep(5);
      } else {
        setResult(data.booking);
        clear();
        setStep(5);
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Moyasar hosted form
  useEffect(() => {
    if (!moyasarBooking || !payConfig.enabled || !window.Moyasar) return;
    const el = document.querySelector(".mysr-form");
    if (el) el.innerHTML = "";
    try {
      window.Moyasar.init({
        element: ".mysr-form",
        amount: moyasarBooking.amount_halalas,
        currency: "SAR",
        description: `Cloud Beauty ${moyasarBooking.reference}`,
        publishable_api_key: payConfig.publishable_key,
        callback_url: `${window.location.origin}/payment-result?booking_id=${moyasarBooking.id}`,
        methods: payConfig.methods,
        supported_networks: ["mada", "visa", "mastercard"],
        on_completed: async (payment) => {
          await api.post("/payments/record-attempt", { booking_id: moyasarBooking.id, payment_id: payment.id });
        },
      });
    } catch (e) { /* ignore */ }
  }, [moyasarBooking, payConfig]);

  return (
    <div className="pt-24 sm:pt-32 pb-28 sm:pb-8">
      <div className="px-4 sm:px-8 lg:px-16 max-w-5xl mx-auto">
        {/* Stepper */}
        {!(result || moyasarBooking) && (
          <div className="flex items-center justify-center gap-1 sm:gap-3 mb-6 sm:mb-10" data-testid="booking-stepper">
            {steps.map((s, i) => (
              <div key={s.n} className="flex items-center">
                <div className="flex flex-col items-center gap-1.5">
                  <div className={`grid place-items-center h-9 w-9 sm:h-10 sm:w-10 rounded-full transition-colors ${
                    step >= s.n ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  }`}>
                    <s.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${step >= s.n ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                </div>
                {i < steps.length - 1 && <div className={`w-5 sm:w-14 h-0.5 mx-1 ${step > s.n ? "bg-primary" : "bg-border"}`} />}
              </div>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>

            {/* STEP 1: SERVICES */}
            {step === 1 && (
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 rounded-3xl bg-card cb-card-shadow p-4 sm:p-6">
                  <h2 className="text-xl font-bold mb-1">{t.booking.details}</h2>
                  <p className="text-muted-foreground text-sm mb-5">{t.booking.chooseServices}</p>
                  {services.length === 0 && <p className="text-muted-foreground text-center py-8">{t.booking.selectServicesFirst}</p>}
                  <div className="space-y-3">
                    {services.map((s) => (
                      <div key={s.id} className="flex items-center gap-3 p-3 rounded-2xl bg-muted/60" data-testid={`cart-item-${s.id}`}>
                        <img src={s.image_url?.startsWith("http") ? s.image_url : ""} alt="" className="h-14 w-14 rounded-xl object-cover bg-muted" />
                        <div className="flex-1">
                          <div className="font-semibold">{L(s, "name")}</div>
                          <div className="text-sm text-muted-foreground">{s.price} {t.common.sar} · {s.duration} {t.common.minutes}</div>
                        </div>
                        <button onClick={() => removeService(s.id)} className="p-2 rounded-full hover:bg-background" data-testid={`cart-remove-${s.id}`}>
                          <X className="h-5 w-5 text-muted-foreground" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" onClick={() => setShowAdd((v) => !v)} className="mt-4 rounded-full border-2 w-full h-12 font-semibold" data-testid="add-service-toggle">
                    <Plus className="h-4 w-4 me-2" /> {t.booking.addMore}
                  </Button>
                  {showAdd && (
                    <div className="mt-4 grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                      {allServices.filter((a) => !services.find((s) => s.id === a.id)).map((a) => (
                        <button key={a.id} onClick={() => { addService(a); }} className="flex items-center gap-2 p-2.5 rounded-xl hover:bg-muted active:bg-muted text-start" data-testid={`add-option-${a.id}`}>
                          <img src={a.image_url?.startsWith("http") ? a.image_url : ""} alt="" className="h-10 w-10 rounded-lg object-cover bg-muted" />
                          <span className="flex-1 text-sm font-medium">{L(a, "name")}</span>
                          <span className="text-sm text-primary font-semibold">{a.price}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="rounded-3xl bg-card cb-card-shadow p-4 sm:p-6 h-fit">
                  <h3 className="font-bold mb-4">{t.booking.summary}</h3>
                  {offer && (
                    <div className="flex justify-between items-center text-sm mb-2 p-2 rounded-xl bg-secondary/60">
                      <span>{L(offer, "title")}</span>
                      <button onClick={() => setOffer(null)}><X className="h-4 w-4" /></button>
                    </div>
                  )}
                  <div className="flex justify-between text-muted-foreground"><span>{t.booking.subtotal}</span><span>{sub} {t.common.sar}</span></div>
                  {discount > 0 && <div className="flex justify-between text-destructive mt-1"><span>{t.booking.discount}</span><span>- {discount}</span></div>}
                  <div className="flex justify-between text-xl font-bold mt-3 pt-3 border-t border-border"><span>{t.booking.total}</span><span className="text-primary">{total} {t.common.sar}</span></div>
                </div>
              </div>
            )}

            {/* STEP 2: DATE & TIME */}
            {step === 2 && (
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="rounded-3xl bg-card cb-card-shadow p-3 sm:p-6 grid place-items-center">
                  <Label className="self-start mb-3 font-semibold">{t.booking.chooseDate}</Label>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { if (d) { setDate(d); loadSlots(d); } }}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    className="rounded-2xl"
                    data-testid="booking-calendar"
                  />
                </div>
                <div className="rounded-3xl bg-card cb-card-shadow p-4 sm:p-6">
                  <Label className="mb-3 block font-semibold">{t.booking.chooseTime}</Label>
                  {!date ? (
                    <p className="text-muted-foreground text-center py-12">{t.booking.selectDateFirst}</p>
                  ) : slots === null ? (
                    <div className="grid place-items-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                  ) : slots.filter((s) => s.available).length === 0 ? (
                    <p className="text-muted-foreground text-center py-12">{t.booking.noSlots}</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-slots">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          disabled={!s.available}
                          onClick={() => setTime(s.time)}
                          data-testid={`slot-${s.time}`}
                          className={`py-3 sm:py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                            time === s.time ? "bg-primary text-primary-foreground"
                            : s.available ? "bg-muted hover:bg-secondary text-foreground"
                            : "bg-muted/40 text-muted-foreground/40 line-through cursor-not-allowed"
                          }`}
                          dir="ltr"
                        >
                          {s.time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 3: CUSTOMER */}
            {step === 3 && (
              <div className="rounded-3xl bg-card cb-card-shadow p-4 sm:p-8 max-w-2xl mx-auto">
                <h2 className="text-xl font-bold mb-5">{t.booking.yourInfo}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label className="mb-1.5 block">{t.booking.fullName} *</Label>
                    <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="rounded-xl h-12 text-base" autoComplete="name" data-testid="customer-name-input" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">{t.booking.phone} *</Label>
                    <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="rounded-xl h-12 text-base" dir="ltr" inputMode="tel" type="tel" placeholder="05XXXXXXXX" data-testid="customer-phone-input" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">{t.booking.email} *</Label>
                    <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="rounded-xl h-12 text-base" dir="ltr" inputMode="email" autoComplete="email" placeholder="example@gmail.com" data-testid="customer-email-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="mb-1.5 block">{t.booking.contactMethod}</Label>
                    <RadioGroup value={customer.contact_method} onValueChange={(v) => setCustomer({ ...customer, contact_method: v })} className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="whatsapp" data-testid="contact-whatsapp" /> {t.booking.whatsapp}</label>
                      <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="call" data-testid="contact-call" /> {t.booking.call}</label>
                    </RadioGroup>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="mb-1.5 block">{t.booking.notes}</Label>
                    <Textarea value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} className="rounded-xl text-base" rows={3} data-testid="customer-notes-input" />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4: SUMMARY */}
            {step === 4 && (
              <div className="rounded-3xl bg-card cb-card-shadow p-4 sm:p-8 max-w-2xl mx-auto">
                <h2 className="text-xl font-bold mb-5">{t.booking.summary}</h2>
                <div className="space-y-2">
                  {services.map((s) => (
                    <div key={s.id} className="flex justify-between py-2 border-b border-border/60">
                      <span>{L(s, "name")} <span className="text-muted-foreground text-sm">· {s.duration} {t.common.minutes}</span></span>
                      <span className="font-medium">{s.price} {t.common.sar}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">{t.booking.chooseDate}</span><span>{fmt(date)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t.booking.chooseTime}</span><span dir="ltr">{time}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t.booking.fullName}</span><span>{customer.name}</span></div>
                </div>
                <div className="mt-4 pt-4 border-t border-border space-y-1">
                  <div className="flex justify-between text-muted-foreground"><span>{t.booking.subtotal}</span><span>{sub} {t.common.sar}</span></div>
                  {discount > 0 && <div className="flex justify-between text-destructive"><span>{t.booking.discount}</span><span>- {discount}</span></div>}
                  <div className="flex justify-between text-xl font-bold pt-2"><span>{t.booking.total}</span><span className="text-primary">{total} {t.common.sar}</span></div>
                </div>

                {payConfig.enabled && (
                  <div className="mt-5">
                    <Label className="mb-2 block font-semibold">{t.booking.paymentMethod}</Label>
                    <RadioGroup value={payMethod} onValueChange={setPayMethod} className="grid grid-cols-2 gap-2">
                      <label className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer ${payMethod === "pay_at_salon" ? "border-primary bg-secondary/40" : "border-border"}`}><RadioGroupItem value="pay_at_salon" data-testid="pay-at-salon" /> {t.booking.payAtSalon}</label>
                      <label className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer ${payMethod === "pay_online" ? "border-primary bg-secondary/40" : "border-border"}`}><RadioGroupItem value="pay_online" data-testid="pay-online" /> {t.booking.payOnline}</label>
                    </RadioGroup>
                  </div>
                )}

                <label className="flex items-start gap-3 mt-6 cursor-pointer">
                  <Checkbox checked={agree} onCheckedChange={setAgree} className="mt-0.5" data-testid="agree-policy-checkbox" />
                  <span className="text-sm text-muted-foreground">{t.booking.agree}</span>
                </label>
              </div>
            )}

            {/* STEP 5: CONFIRMATION / PAYMENT */}
            {step === 5 && (
              <div className="max-w-2xl mx-auto">
                {moyasarBooking && !result ? (
                  <div className="rounded-3xl bg-card cb-card-shadow p-6 sm:p-8">
                    <h2 className="text-xl font-bold mb-4 text-center">{t.booking.payOnline}</h2>
                    <div className="mysr-form" data-amount={moyasarBooking.amount_halalas} />
                  </div>
                ) : (
                  <div className="text-center">
                    <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" />
                    <h1 className="mt-4 text-3xl font-bold text-foreground">{t.booking.successTitle}</h1>
                    <p className="text-muted-foreground mt-2">{t.booking.successSub}</p>
                    <div className="mt-8 text-start"><Invoice booking={result} /></div>
                    <Button onClick={() => { setStep(1); setResult(null); setDate(null); setTime(""); setAgree(false); }} className="mt-6 rounded-full font-semibold no-print" data-testid="new-booking-btn">
                      {t.booking.newBooking}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* NAV BUTTONS */}
        {step < 5 && (
          <div className="fixed sm:static bottom-0 inset-x-0 z-40 cb-glass sm:bg-transparent sm:backdrop-blur-none border-t border-border sm:border-0 px-3 pt-2.5 sm:p-0 flex gap-2 justify-between sm:mt-8 max-w-2xl mx-auto lg:max-w-none" style={{ paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))" }} data-testid="booking-nav-bar">
            <Button variant="outline" onClick={() => step === 1 ? nav("/services") : setStep(step - 1)} className="rounded-full border-2 px-5 sm:px-6 h-12 sm:h-10 font-semibold" data-testid="booking-back-btn">
              <ArrowRight className="h-4 w-4 me-2 rtl-flip" /> {t.common.back}
            </Button>
            {step < 4 ? (
              <Button onClick={() => canNext ? setStep(step + 1) : toast.error(t.common.error)} disabled={!canNext} className="flex-1 sm:flex-none rounded-full px-8 h-12 sm:h-10 text-base sm:text-sm font-semibold" data-testid="booking-next-btn">
                {t.common.next} <ArrowLeft className="h-4 w-4 ms-2 rtl-flip" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={!canNext || submitting} className="flex-1 sm:flex-none rounded-full px-8 h-12 sm:h-10 text-base sm:text-sm font-semibold" data-testid="booking-confirm-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                {payMethod === "pay_online" && payConfig.enabled ? t.booking.payOnline : t.booking.confirmBooking}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
